<template>
  <ModalDialog
    header-text="Product Details"
    contentClass="product-details-modal"
    @close-modal="closeModal"
    open
  >
    <template v-slot:content>
      <div class="agency-id-label">Unique ID: {{ product.AgencyId }}</div>
      <div class="flex-container">
        <Grid
          :data-items="productProperties"
          :columns="columns"
          :scrollable="'none'"
          data-cy="product-details-grid"
        >
          <template v-slot:emptyHeader="{}"> </template>
        </Grid>
      </div>
    </template>
  </ModalDialog>
</template>

<script setup lang="ts">
import { EnumDpm } from "@/Domain/EnumDpm";
import type { ProductDisplay } from "@/Domain/ProductDisplay";
import { Grid, type GridColumnProps } from "@progress/kendo-vue-grid";
import { onBeforeMount, ref } from "vue";
import {
  getConsumerIndustrialString,
  getCertificationEndDateString,
} from "@/shared/helpers/productsHelper";
import ModalDialog from "@/shared/ui/modal/ModalDialog.vue";
import { api } from "@/api";
import { useMainStore } from "@/stores/useMainStore";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";

const main = useMainStore();

const props = defineProps<{
  agencyId: string;
}>();

const emit = defineEmits(["closeModal"]);

const productProperties = ref<{ name: string; value: string }[]>([]);
const product = ref<ProductDisplay>({} as ProductDisplay);

const columns: GridColumnProps[] = [
  {
    field: "name",
    headerCell: "emptyHeader",
    className: "name-row",
  },
  {
    field: "value",
    headerCell: "emptyHeader",
  },
];

const setProductProperties = function () {
  productProperties.value = [
    {
      name: "Agency",
      value: product.value.AgencyName,
    },
    {
      name: "Product",
      value: product.value.LabelName,
    },
    {
      name: "Product Number",
      value: product.value.LabelNumber,
    },
    {
      name: "Company",
      value: product.value.Company,
    },
    {
      name: "Brand",
      value: product.value.BrandName,
    },
    { name: "Symbol", value: product.value.Symbol },
    {
      name: "DPM Status",
      value: EnumDpm[product.value._Dpm || -1],
    },
    {
      name: "Conditions",
      value: product.value.Conditions,
    },
    {
      name: "Consumer/Industrial",
      value: getConsumerIndustrialString(product.value),
    },
    {
      name: "Comments",
      value: product.value.Comments,
    },
    {
      name: "Passover",
      value: product.value._Pesach ? "Y" : "N",
    },
    {
      name: "Passover Symbol",
      value: product.value.PassoverSymbol,
    },
    {
      name: "Certified Until",
      value: getCertificationEndDateString(product.value),
    },
    {
      name: "Web Site",
      value: product.value.WebSite,
    },
  ];
};

const updateMatchingProductAsync = async function () {
  await handleResponsePromiseAsync(
    api.get("GetMatchingProductsByUkdId", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      ukdId: props.agencyId,
      includeConfidentials: true,
      caller: "",
    }),
    (data) => {
      product.value = data.ListItems[0];
      setProductProperties();
    },
    "UkdIngredientDetailsModal.vue",
    "getMatchingProductAsync"
  );
};

setProductProperties();

const closeModal = function () {
  emit("closeModal");
};

onBeforeMount(async () => {
  await updateMatchingProductAsync();
});
</script>

<style scoped lang="scss">
dialog {
  width: 100%;
  height: 100%;
  top: 0;
  border: none;
  background: none;

  .modal-bg {
    position: fixed;
    z-index: 100;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    background: rgba(0, 0, 0, 0.4);
  }

  .modal-content {
    border: none;
    border-top: 7px solid #4a90e2;
    padding: 20px;
    width: 640px;
    left: 50%;
    top: 50%;
    transform: translateY(-50%);
    margin-left: -320px;
    position: fixed;
    display: block;

    background-color: white;
    box-shadow: 0 2px 8px rgbal(0, 0, 0, 0.26);
    z-index: 1000;

    > header {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .modal__close {
      font-size: 18px;
      padding: 10px;
      cursor: pointer;
      &:hover {
        color: #444;
      }
    }

    .agency-id-label {
      font-size: 14px;
      font-weight: 600;
      margin-bottom: 10px;
    }
  }
}

.flex-container {
  display: flex;
  flex-wrap: nowrap;
  max-width: 500px;
  margin-top: 10px;
  margin-bottom: 20px;
}
.flex__left {
  flex: 1;
  margin-right: 40px;
}
.flex__right {
  flex: 3;
}
.grid-container {
  overflow: auto;
  max-height: 440px;
  border: 1px solid #ddd;
}
.form-row {
  display: flex;
  margin-bottom: 10px;
  align-items: center;
}
.form-row label {
  font-weight: 200;
  text-align: right;
  min-width: 125px;
  margin-right: 20px;
}

td {
  font-size: 13px;
}
</style>

<style lang="scss">
.product-details-modal {
  .flex-container .k-grid-header {
    display: none !important;
  }
}
</style>
